import Button from '@/components/Button'
import DateDisplay from '@/components/DateDisplay'
import Layout, { Stack } from '@/components/Layout'
import { rbp } from '@/constants/measured-scope'
import { ContextInterface as BookingContextInterface } from '@/context/booking'
import { ContextInterface as CancelContextInterface } from '@/context/cancelBooking'
import { lobbyUrl } from '@/lib/lobbyUrl'
import { JoinBookedSessionCardAction, SessionCard } from '@/shared/api-generated-types'
import { useMediaQuery } from '@react-hookz/web'
import { useTranslation } from 'next-i18next'
import { useCallback, useEffect, useState } from 'react'
import { CardPrimaryAction, CardSecondaryAction } from '../ClassCard'
import * as styles from './styles'

export interface Props {
  session: Partial<SessionCard>
  bookingState: BookingContextInterface['state']
  cancelState: CancelContextInterface['state']
  handleBookingAction: (session: Partial<SessionCard>) => void
  handleCancelBookingAction: (sessionId: string | undefined) => void
}

const BookSession = ({
  session,
  bookingState,
  cancelState,
  handleBookingAction,
  handleCancelBookingAction,
}: Props): JSX.Element => {
  const { primaryAction, secondaryAction, sessionId, start, end } = session
  const { t } = useTranslation('classes')
  const [buttonLabel, setButtonLabel] = useState<string | undefined>(primaryAction?.label && t('slotCard.book'))
  const [cancelButtonLabel, setCancelButtonLabel] = useState<string | undefined>(
    secondaryAction?.label && t('slotCard.cancel'),
  )
  const [isLobbyButton, setIsLobbyButton] = useState<boolean>(
    primaryAction?.__typename === 'JoinBookedSessionCardAction' ||
      primaryAction?.__typename === 'JoinLiveSessionCardAction',
  )
  const [isBooking, setIsBooking] = useState<boolean>(false)
  const [isBooked, setIsBooked] = useState<boolean>(false)
  const [bookingFailed, setBookingFailed] = useState<boolean>(false)
  const isSmallDevice = useMediaQuery(`only screen and (max-width : ${rbp.smallTablet})`, {
    initializeWithValue: false,
  })

  const bookedPrimaryAction = {
    __typename: 'JoinBookedSessionCardAction',
    url: lobbyUrl(`/session/${sessionId}`),
    label: t('slotCard.enter'),
  } as JoinBookedSessionCardAction

  // TODO: Revisit the state mgmt as mentioned here: https://github.com/getsetup-io/gsu-web/pull/65#discussion_r758047383
  useEffect(() => {
    if (bookingState.fetching?.find((s) => s === sessionId)) {
      setButtonLabel(t('slotCard.booking'))
      setIsBooking(true)
    } else if (bookingState.booked?.find((s) => s === sessionId)) {
      setButtonLabel(t('slotCard.booked'))
      setIsBooked(true)
      setIsBooking(false)
    } else if (bookingState.failed?.find((s) => s === sessionId)) {
      setButtonLabel(t('slotCard.bookingFailed'))
      setBookingFailed(true)
      setIsBooking(false)
    }
  }, [bookingState])

  useEffect(() => {
    if (cancelState.fetching?.find((s) => s === sessionId)) {
      setCancelButtonLabel(t('slotCard.cancelling'))
    } else if (cancelState.cancelled?.find((s) => s === sessionId)) {
      setCancelButtonLabel(t('slotCard.cancel'))
      setIsLobbyButton(false)
      setButtonLabel(t('slotCard.book'))
    } else if (cancelState.failed?.find((s) => s === sessionId)) {
      setCancelButtonLabel(t('slotCard.cancelFailed'))
    }
  }, [cancelState])

  // After 1.5 seconds, lets show the next action for a user to take
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isBooked) {
        setIsBooked(false)
        setButtonLabel(t('slotCard.enter'))
        setIsLobbyButton(true)
      }

      if (bookingFailed) {
        setBookingFailed(false)
        setButtonLabel(t('slotCard.book'))
      }
    }, 1500)
    return () => clearTimeout(timer)
  }, [isBooked, bookingFailed])

  const handleCancel = useCallback(
    (sessionId: string | undefined) => () => {
      if (sessionId) handleCancelBookingAction(sessionId)
    },
    [sessionId],
  )
  const handleBooking = useCallback(
    (session: Partial<SessionCard>) => () => {
      if (session) handleBookingAction(session)
    },
    [session],
  )

  return (
    <Layout className={styles.root} spacing="sm">
      <Stack x>
        <Stack y>
          {start && (
            <DateDisplay
              color="dark"
              value={start}
              end={end}
              isCommunityLedClass={session.isCommunityLedClass}
              isEncore={session.isEncore}
              livenessType={session.livenessType}
            />
          )}
          {/* TODO: Add seatsLeft once available in API */}
          {/* <Stack x justifyContent="flex-start">
            <img src="/icons/people.svg" className={styles.icon} loading="lazy" />
            <Text size="xxxs" weight="sm" className="pl-sm">
              {t('slotCard.seatLeft', seatLeft)}
            </Text>
          </Stack> */}
        </Stack>

        {isLobbyButton ? (
          secondaryAction ? (
            <Stack x spaceBetweenX="sm">
              <CardSecondaryAction secondaryAction={secondaryAction} cancelBooking={handleCancel(session.sessionId)}>
                <Button size={isSmallDevice ? 'sm' : 'md'} theme="primaryOutline" label={cancelButtonLabel} />
              </CardSecondaryAction>

              <CardPrimaryAction primaryAction={bookedPrimaryAction}>
                <Button size={isSmallDevice ? 'sm' : 'md'} theme="secondary" label={bookedPrimaryAction.label} />
              </CardPrimaryAction>
            </Stack>
          ) : (
            <CardPrimaryAction primaryAction={bookedPrimaryAction}>
              <Button size={isSmallDevice ? 'sm' : 'md'} theme="secondary" label={bookedPrimaryAction.label} />
            </CardPrimaryAction>
          )
        ) : bookingFailed ? (
          <Button theme="white" disabled label={buttonLabel} size={isSmallDevice ? 'sm' : 'md'} />
        ) : isBooked ? (
          <Button
            iconLeft="double-tick"
            theme="secondaryOutline"
            label={buttonLabel}
            size={isSmallDevice ? 'sm' : 'md'}
          />
        ) : (
          <CardPrimaryAction primaryAction={primaryAction} makeBooking={handleBooking(session)}>
            <Button
              size={isSmallDevice ? 'sm' : 'md'}
              theme="primary"
              disabled={isBooked || bookingFailed || isBooking}
              label={buttonLabel}
            />
          </CardPrimaryAction>
        )}
      </Stack>
    </Layout>
  )
}
export default BookSession
